import {toast} from 'react-toastify';
import React, {FC, ReactNode, useState} from 'react';
import {IUpdateUserPreferencesRequest} from '@verdocs/js-sdk';
import {AdjustmentsHorizontalIcon} from '@heroicons/react/24/outline';
import {Popover, PopoverAnchor, PopoverContent, PopoverTrigger, Switch} from '../../Components';
import {saveMyPreferences, useMyUser} from '../../react-sdk/queries';

const PreferenceRow: FC<{title: string; checked: boolean; disabled: boolean; onChange: (checked: boolean) => void; children?: ReactNode}> = ({
  title,
  checked,
  disabled,
  onChange,
  children,
}) => (
  <label className="flex cursor-pointer items-start justify-between gap-6 border-b border-[#f2f3f5] py-3 last:border-0">
    <span>
      <span className="block text-[13px] font-medium text-[#33364b]">{title}</span>
      {children && <span className="mt-0.5 block text-[12px] leading-relaxed text-gray-500">{children}</span>}
    </span>
    <Switch
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
      className="mt-0.5 data-[state=checked]:bg-[#654dcb] data-[state=unchecked]:bg-gray-200"
    />
  </label>
);

// The menu opens beside the whole row it sits in, so it never covers the links next to the button.
export const PreferencesMenu: FC<{children?: ReactNode}> = ({children}) => {
  const preferences = useMyUser().data?.preferences;
  const [saving, setSaving] = useState(false);
  const highContrast = typeof preferences?.high_contrast === 'boolean' ? preferences.high_contrast : window.matchMedia?.('(prefers-contrast: more)').matches;

  const save = (changes: IUpdateUserPreferencesRequest) => {
    setSaving(true);
    saveMyPreferences(changes)
      .catch((e) => {
        console.log('Error saving preferences', e);
        toast.error('Error saving preferences. Please try again.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <Popover>
      <PopoverAnchor asChild>
        <div className="flex w-full flex-row items-center justify-between pl-4">
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Preferences"
              aria-label="Preferences"
              className="grid size-8 flex-none place-items-center rounded-[7px] border border-white/20 text-gray-200 hover:bg-white/10 data-[state=open]:border-white/40 data-[state=open]:bg-white/15">
              <AdjustmentsHorizontalIcon className="size-[18px]" />
            </button>
          </PopoverTrigger>
          {children}
        </div>
      </PopoverAnchor>
      <PopoverContent side="right" align="end" sideOffset={12} className="w-[290px] rounded-xl bg-white px-4 pb-1 pt-3.5 text-[#33364b]">
        <div className="text-[13px] font-bold">Preferences</div>
        <PreferenceRow
          title="New Builder"
          checked={preferences?.template_builder === 'builder'}
          disabled={saving}
          onChange={(checked) => save({template_builder: checked ? 'builder' : 'build'})}
        />
        <PreferenceRow title="High contrast" checked={!!highContrast} disabled={saving} onChange={(checked) => save({high_contrast: checked})}>
          Matches your system setting until you change it here.
        </PreferenceRow>
      </PopoverContent>
    </Popover>
  );
};
